import React, { useCallback, useEffect, useRef } from 'react';

import { Spin } from 'antd';
import { GetMessagesUpdatesStatsQuery } from 'api/server';
import { createCn } from 'bem-react-classname';
import Chart from 'chart.js';
import cn from 'classnames';
import moment from 'moment-timezone';
import { useDispatch, useSelector } from 'react-redux';
import { AppThunk, server } from 'store';

import { getDashboardTimeChartConfig } from '../../utils/chartOptionsUtil';

import './MessagesUpdatesStatsChart.scss';

const cx = createCn('MessagesUpdatesStatsChart');

interface Props {
  className?: string;
  header?: string;
  fromDate: string;
}

export const MessagesUpdatesStatsChart: React.FC<Props> = ({
  className,
  header,
  fromDate,
}) => {
  const dispatch = useDispatch();
  const chartRef = useRef<HTMLCanvasElement>(null);

  const getMessagesUpdatesStatsChartThunk = useCallback(
    (query: GetMessagesUpdatesStatsQuery): AppThunk => async (
      dispatch,
      getState,
    ) => {
      await dispatch(
        server.getMessagesUpdatesStats.thunk.request({
          query,
        }),
      );

      const { data } = server.getMessagesUpdatesStats.selector.state(
        getState(),
      );

      if (data && chartRef.current) {
        new Chart(
          chartRef.current,
          getDashboardTimeChartConfig({
            borderColor: '#18f0aa',
            yLabelString: 'Messages',
            data: data.list.map((item) => ({
              x: item.date,
              y: item.amount,
            })),
          }),
        );
      }
    },
    [chartRef],
  );

  const { isFetching, isFetched } = useSelector(
    server.getMessagesUpdatesStats.selector.state,
  );

  useEffect(() => {
    dispatch(
      getMessagesUpdatesStatsChartThunk({
        timezone: moment.tz.guess(true),
        fromDate: moment(fromDate).startOf('day').format(),
      }),
    );
  }, [dispatch, fromDate, getMessagesUpdatesStatsChartThunk]);

  return (
    <div className={cn(className, cx())}>
      <div className={cx('container')}>
        {header && (
          <div className={cx('header')}>
            <h3>{header}</h3>
          </div>
        )}

        <div className={cx('graphArea')}>
          {isFetching && !isFetched && (
            <div className={cx('spin')}>
              <Spin size="large" />
            </div>
          )}
          <canvas ref={chartRef} />
        </div>
      </div>
    </div>
  );
};
